import type {
  CommandKind,
  CommandPayloads,
  CountResult,
  FailedEntry,
  OpenResult,
  PayInCategory,
  PayOutCategory,
  ShiftMovementView,
  ShiftStateView,
  ShiftView,
} from '@didto/domain';
import {
  PAY_IN_CATEGORIES,
  PAY_IN_LABELS,
  PAY_OUT_CATEGORIES,
  PAY_OUT_LABELS,
  describeOverShort,
  formatInTimeZone,
  formatMinor,
  fractionDigitsFor,
  minorToInput,
  parseMoneyToMinor,
  removedMinor,
  suggestedKeepMinor,
  uuidv7,
} from '@didto/domain';
import {
  Badge,
  Button,
  ErrorState,
  Field,
  Input,
  KvRow,
  Loading,
  Notice,
  Sheet,
  Stat,
  Textarea,
} from '@didto/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useToast } from '@/app/toast';
import type { AppError } from '@/lib/errors';
import { errorRecord, errorText, toAppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { hintOf } from '@/lib/orders';
import {
  employeeNameOf,
  entryAmountMinor,
  heldOnThisTablet,
  outbox,
  runQueuedCommand,
  useFailedEntries,
  useOutboxStatus,
} from '@/lib/outbox';
import { fetchShiftSummary, shiftKeys } from '@/lib/shifts';
import { LEDGER_ROW_TONE } from '@/lib/status';
import { approvalCopy, approvalRequest } from './approvals';
import type { Approval, ApprovalRequest } from './sheets';
import { ApprovalSheet } from './sheets';
import { ACTION_LABEL, ShiftSummaryView, overShortTone } from './shiftSummary';
import './shift.css';

/**
 * The cashier's shift on the POS tablet (Phase 7A, ADR-024). Kept deliberately simple: PIN, open
 * the shift with a count of the drawer, the normal POS, and a Shift menu with Pay in, Pay out, No
 * sale, Close shift, Hand over and, only when the location has a safe, Safe drop. Every count is
 * blind: the tablet never shows what the drawer should hold before the count is final. The server
 * decides everything; this component only explains it. While the server cannot be reached nothing
 * here can be started (ADR-037): every shift and cash action is disabled until it is back.
 */

type Outcome = { ok: true; result: Record<string, unknown> } | { ok: false; error: AppError };

/**
 * Command ids of operations whose last attempt got no answer (a dropped connection), by operation:
 * the retry reuses the id, so a command that did arrive is never recorded twice, even after its
 * sheet was closed and opened again. Cleared on any definite answer.
 */
const unanswered = new Map<string, string>();

/** Shift and cash commands go straight to the server (never queued). `key` names the operation
 * (for cash entries it includes what was entered). */
function useShiftCommand() {
  const qc = useQueryClient();
  return async <K extends CommandKind>(
    key: string,
    kind: K,
    payload: CommandPayloads[K],
  ): Promise<Outcome> => {
    const id = unanswered.get(key) ?? uuidv7();
    unanswered.set(key, id);
    try {
      const result = await runQueuedCommand(kind, payload, { commandId: id });
      unanswered.delete(key);
      return { ok: true, result };
    } catch (e) {
      const error = toAppError(e);
      // a refused command was not recorded: the next attempt is a new command
      if (error.code !== 'network') unanswered.delete(key);
      if (error.code === 'command_conflict')
        return {
          ok: false,
          error: {
            ...error,
            message: 'That was already recorded. Check the screen before doing it again.',
          },
        };
      return { ok: false, error };
    } finally {
      void qc.invalidateQueries({ queryKey: shiftKeys.all });
    }
  };
}

function parseAmount(text: string, currency: string): number | null {
  return text.trim()
    ? parseMoneyToMinor(text, { fractionDigits: fractionDigitsFor(currency) })
    : null;
}

/** An amount of money: right-aligned tabular figures; `large` for a blind count of the drawer. */
function MoneyField({
  label,
  value,
  onChange,
  help,
  testId,
  large,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
  testId: string;
  large?: boolean;
}) {
  return (
    <Field label={label} {...(help ? { help } : {})}>
      {(id) => (
        <Input
          id={id}
          className={large ? 'd-input--money shift-count-input' : 'd-input--money'}
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      )}
    </Field>
  );
}

function TextField({
  label,
  value,
  onChange,
  help,
  testId,
  multiline,
  maxLength = 200,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
  testId: string;
  multiline?: boolean;
  maxLength?: number;
}) {
  return (
    <Field label={label} {...(help ? { help } : {})}>
      {(id) =>
        multiline ? (
          <Textarea
            id={id}
            rows={3}
            maxLength={maxLength}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
        ) : (
          <Input
            id={id}
            maxLength={maxLength}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
        )
      }
    </Field>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  labels,
  onChange,
  testId,
}: {
  label: string;
  value: T | null;
  options: readonly T[];
  labels: Record<T, string>;
  onChange: (v: T) => void;
  testId: string;
}) {
  return (
    <div className="d-field">
      <span className="d-label">{label}</span>
      <div className="d-pills" role="group" aria-label={label} data-testid={testId}>
        {options.map((o) => (
          <button
            key={o}
            type="button"
            className="d-pill"
            aria-pressed={value === o}
            onClick={() => onChange(o)}
          >
            {labels[o]}
          </button>
        ))}
      </div>
    </div>
  );
}

const CLOSE_KIND_TEXT: Record<string, string> = {
  cashier: 'closed',
  owner: 'closed by the owner',
  handover: 'handed over',
  takeover: 'taken over',
};

type SheetName = 'menu' | 'open' | 'pay_in' | 'pay_out' | 'no_sale' | 'safe_drop';
type FlowMode = 'close' | 'handover' | 'takeover';

/**
 * The shift around the POS: what the drawer is doing (banner), the Shift menu, the open, close,
 * hand-over and take-over flows, cash entries, and notices about payments this tablet still holds.
 * `children` is the POS itself; it is replaced by the "Open shift" screen until a shift is open.
 */
export function ShiftDesk({
  state,
  currency,
  timeZone,
  children,
}: {
  state: ShiftStateView | null | undefined;
  currency: string;
  timeZone: string;
  children: ReactNode;
}) {
  const [sheet, setSheet] = useState<SheetName | null>(null);
  // the shift as it was when the flow started: a close ends it, and its summary stays on screen
  const [flow, setFlow] = useState<{ mode: FlowMode; key: string; shift: ShiftView } | null>(null);
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [summaryId, setSummaryId] = useState<string | null>(null);
  const [notNowFor, setNotNowFor] = useState<string | null>(null);
  const online = useOnline();
  const money = (minor: number) => formatMinor(minor, currency);
  const time = (iso: string) => formatInTimeZone(iso, timeZone);

  if (!state) return <>{children}</>;
  const me = state.employee_id;
  const shift = state.shift;
  const perms = state.permissions ?? { operate: false, move: false, cover: false, receive: false };

  const startFlow = (mode: FlowMode) => {
    if (!shift) return;
    setSheet(null);
    setFlow({ mode, key: uuidv7(), shift });
  };
  const flowMode = (s: ShiftView): FlowMode =>
    s.closing?.kind === 'handover'
      ? 'handover'
      : s.closing?.kind === 'takeover'
        ? 'takeover'
        : 'close';

  const last = state.last_closed ?? null;

  /* ── what the drawer is doing ── */
  let banner: ReactNode = null;
  let gate: ReactNode = null;
  if (!state.drawer) {
    if (state.terminal_kind === 'pos')
      banner = (
        <Notice tone="warning" role="status" testId="shift-no-drawer">
          This tablet is not linked to a cash drawer, so it takes no payments. The owner links it
          under Devices.
        </Notice>
      );
  } else if (!shift) {
    const showGate = perms.operate && me !== null && notNowFor !== me;
    if (showGate)
      gate = (
        <div className="d-gate d-surface shift-gate" data-testid="shift-gate">
          <div className="d-eyebrow">{state.drawer.name}</div>
          <h2>No shift is open</h2>
          <p className="d-muted">Open your shift by counting the cash in the drawer.</p>
          <Button
            variant="primary"
            size="lg"
            disabled={!online}
            onClick={() => setSheet('open')}
            data-testid="shift-open"
          >
            Open shift
          </Button>
          <span className="d-inline">
            {last && (
              <Button variant="quiet" onClick={() => setSummaryId(last.id)}>
                Last shift summary
              </Button>
            )}
            <Button variant="quiet" onClick={() => setNotNowFor(me)} data-testid="shift-not-now">
              Not now
            </Button>
          </span>
        </div>
      );
    else
      banner = (
        <Notice
          tone="warning"
          role="status"
          testId="shift-none"
          action={
            perms.operate && (
              <Button
                size="sm"
                variant="primary"
                disabled={!online}
                onClick={() => setSheet('open')}
              >
                Open shift
              </Button>
            )
          }
        >
          No shift is open on {state.drawer.name}: payments wait until one is opened.
        </Notice>
      );
  } else {
    const name = shift.cashier.name;
    const takeoverNow = Date.parse(shift.takeover_at) <= Date.parse(state.server_time);
    const takeoverButton = perms.operate && !shift.mine && (
      <Button
        size="sm"
        disabled={!online}
        onClick={() => startFlow('takeover')}
        data-testid="shift-takeover"
      >
        Take over
      </Button>
    );
    if (shift.status === 'closing' && shift.closing) {
      const cl = shift.closing;
      const canContinue = cl.mine || perms.cover;
      const offerTakeover = !canContinue && cl.started_by.id === shift.cashier.id && takeoverButton;
      banner = (
        <Notice
          tone="warning"
          role="status"
          testId="shift-closing"
          action={
            (canContinue || offerTakeover) && (
              <span className="d-inline">
                {canContinue && (
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!online}
                    onClick={() => setFlow({ mode: flowMode(shift), key: uuidv7(), shift })}
                    data-testid="shift-continue"
                  >
                    {cl.mine ? 'Continue' : 'Continue the count'}
                  </Button>
                )}
                {offerTakeover}
              </span>
            )
          }
        >
          {cl.mine
            ? 'You are counting the drawer.'
            : `${cl.started_by.name} is counting the drawer.`}{' '}
          It takes no payments until the next shift opens.
          {cl.too_long ? ' The count has been open for a long time.' : ''}
        </Notice>
      );
    } else if (shift.stale) {
      const mayClose = shift.mine || perms.cover;
      banner = (
        <Notice
          tone="danger"
          role="alert"
          testId="shift-stale"
          action={
            (mayClose || takeoverButton) && (
              <span className="d-inline">
                {mayClose && (
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!online}
                    onClick={() => startFlow('close')}
                  >
                    {shift.mine ? 'Close shift' : 'Close as owner'}
                  </Button>
                )}
                {takeoverButton}
              </span>
            )
          }
        >
          {shift.mine ? 'Your' : `${name}'s`} shift of {shift.business_date} is still open. Count
          the drawer to close it before taking payments today.
        </Notice>
      );
    } else if (shift.mine || perms.cover) {
      banner = (
        <div className="d-shiftbar shift-strip" data-testid="shift-bar">
          <span className="d-inline shift-strip__facts">
            <Badge tone="success" dot>
              {shift.mine ? 'Shift open' : `${name}'s shift`}
            </Badge>
            <span className="d-muted shift-strip__text">
              <span className="d-strong">{state.drawer.name}</span> · since{' '}
              <span className="d-num">{time(shift.opened_at)}</span>
              {!shift.mine ? ' · you act as the owner' : ''}
            </span>
            {shift.long_open && <Badge tone="warning">Open a long time: close it when done</Badge>}
          </span>
          <Button size="sm" onClick={() => setSheet('menu')} data-testid="shift-menu">
            Shift
          </Button>
        </div>
      );
    } else {
      banner = (
        <Notice tone="warning" role="status" testId="shift-other" action={takeoverButton}>
          {name}&apos;s shift is open on this drawer: only {name} takes payments on it.{' '}
          {takeoverNow
            ? `You can take it over.`
            : `Without the owner it can be taken over from ${time(shift.takeover_at)}; before that the owner approves.`}
        </Notice>
      );
    }
  }

  return (
    <>
      {banner}
      {shift?.mine && shift.others && shift.others.length > 0 && (
        <OwnerActions key={shift.id} shift={shift} money={money} time={time} />
      )}
      <QueueNotices money={money} />
      {gate ?? children}

      <Sheet open={sheet === 'menu'} onClose={() => setSheet(null)} title="Shift">
        {shift && (
          <div className="d-stack" data-testid="shift-menu-sheet">
            {perms.move && (
              <section className="d-stack d-stack--tight" aria-label="Cash">
                <div className="d-eyebrow">Cash</div>
                <div className="shift-menu__grid">
                  <Button
                    size="lg"
                    disabled={!online}
                    onClick={() => setSheet('pay_in')}
                    data-testid="menu-pay-in"
                  >
                    Pay in
                  </Button>
                  <Button
                    size="lg"
                    disabled={!online}
                    onClick={() => setSheet('pay_out')}
                    data-testid="menu-pay-out"
                  >
                    Pay out
                  </Button>
                  <Button
                    size="lg"
                    disabled={!online}
                    onClick={() => setSheet('no_sale')}
                    data-testid="menu-no-sale"
                  >
                    No sale
                  </Button>
                  {state.safe_enabled && (
                    <Button
                      size="lg"
                      disabled={!online}
                      onClick={() => setSheet('safe_drop')}
                      data-testid="menu-safe-drop"
                    >
                      Safe drop
                    </Button>
                  )}
                </div>
              </section>
            )}
            <section className="d-stack d-stack--tight" aria-label="End of shift">
              <div className="d-eyebrow">End of shift</div>
              <Button
                block
                size="lg"
                variant="primary"
                disabled={!online}
                onClick={() => startFlow('close')}
                data-testid="menu-close"
              >
                {shift.mine ? 'Close shift' : 'Close as owner'}
              </Button>
              {shift.mine ? (
                <Button
                  block
                  variant="quiet"
                  disabled={!online}
                  onClick={() => startFlow('handover')}
                  data-testid="menu-handover"
                >
                  Hand over to another cashier
                </Button>
              ) : (
                perms.operate && (
                  <Button
                    block
                    variant="quiet"
                    disabled={!online}
                    onClick={() => startFlow('takeover')}
                    data-testid="menu-takeover"
                  >
                    Take over this shift
                  </Button>
                )
              )}
            </section>
          </div>
        )}
      </Sheet>

      <Sheet open={sheet === 'open'} onClose={() => setSheet(null)} title="Open shift">
        {sheet === 'open' && (
          <OpenFlow state={state} currency={currency} time={time} onOpened={() => setSheet(null)} />
        )}
      </Sheet>

      <Sheet
        open={!!flow}
        onClose={() => setFlow(null)}
        title={
          flow?.mode === 'takeover'
            ? 'Take over the shift'
            : flow?.mode === 'handover'
              ? 'Hand over'
              : 'Close shift'
        }
      >
        {flow && (
          <CountFlow
            key={flow.key}
            mode={flow.mode}
            shift={flow.shift}
            state={state}
            currency={currency}
            timeZone={timeZone}
            onApproval={setApproval}
            onExit={() => setFlow(null)}
          />
        )}
      </Sheet>

      {(['pay_in', 'pay_out', 'no_sale', 'safe_drop'] as const).map((k) => (
        <Sheet key={k} open={sheet === k} onClose={() => setSheet(null)} title={MOVEMENT_TITLE[k]}>
          {sheet === k && shift && (
            <MovementForm
              kind={k}
              shift={shift}
              state={state}
              currency={currency}
              time={time}
              onDone={() => setSheet(null)}
            />
          )}
        </Sheet>
      ))}

      <Sheet open={!!summaryId} onClose={() => setSummaryId(null)} title="Last shift">
        {summaryId && <SummaryLoader shiftId={summaryId} currency={currency} timeZone={timeZone} />}
      </Sheet>
      {/* last, so it stacks above the count sheet that stays open behind it */}
      <ApprovalSheet request={approval} onClose={() => setApproval(null)} />
    </>
  );
}

/* ── opening: a blind count ───────────────────────────────────────────────────────────── */

function OpenFlow({
  state,
  currency,
  time,
  onOpened,
}: {
  state: ShiftStateView;
  currency: string;
  time: (iso: string) => string;
  onOpened: () => void;
}) {
  const send = useShiftCommand();
  const { toast } = useToast();
  const online = useOnline();
  const [amount, setAmount] = useState('');
  // this employee already counted this drawer once: their next count is final
  const [again, setAgain] = useState(!!state.opening?.next_count_final);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const opening = state.opening;
  const prev = opening?.previous ?? null;

  const submit = async () => {
    const minor = parseAmount(amount, currency);
    if (minor === null || minor < 0) {
      setError('Enter the cash counted, for example 2000 or 2000.50');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await send(`open:${state.drawer?.id ?? ''}:${again ? 2 : 1}`, 'shift.open', {
      count_minor: minor,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    const v = r.result as unknown as OpenResult;
    setAmount('');
    if (v.result === 'count_again') {
      setAgain(true);
      return;
    }
    toast('Shift open', 'success');
    onOpened();
  };

  return (
    <div className="d-stack" data-testid="open-shift-sheet">
      <p className="d-muted">
        Count all the cash in {state.drawer?.name ?? 'the drawer'} and enter the total.
      </p>
      {(opening?.first_shift || prev || opening?.counting) && (
        <div className="d-stack d-stack--tight">
          {opening?.first_shift && (
            <p className="d-help">This is the first shift on this drawer.</p>
          )}
          {prev && (
            <p className="d-help">
              Last shift: {prev.cashier}, {CLOSE_KIND_TEXT[prev.close_kind] ?? 'closed'}{' '}
              {time(prev.closed_at)}.
            </p>
          )}
          {opening?.counting && (
            <p className="d-help">
              {opening.counting} started counting this drawer; your count replaces theirs.
            </p>
          )}
        </div>
      )}
      {again && (
        <Notice tone="warning" role="alert" testId="count-again">
          Please count again carefully. This count will be final.
        </Notice>
      )}
      <MoneyField
        label="Cash in the drawer"
        value={amount}
        onChange={setAmount}
        testId="open-count"
        large
      />
      {error && (
        <div className="d-error" role="alert">
          {error}
        </div>
      )}
      <Button
        variant="primary"
        size="lg"
        block
        busy={busy}
        disabled={!online}
        onClick={() => void submit()}
        data-testid="open-start"
      >
        Start shift
      </Button>
    </div>
  );
}

/* ── close, hand over, take over: count, recount, explain, keep, finish ───────────────── */

type Step =
  | { name: 'start' }
  | {
      name: 'pending';
      wait: boolean;
      terminals: { name: string; pending: number }[];
      parked: number;
      message: string;
    }
  | { name: 'count'; final: boolean; again: boolean }
  | { name: 'explain'; counted: number; overShort: number }
  | {
      name: 'finish';
      counted: number;
      overShort: number;
      withinTolerance: boolean;
      reason: string | null;
    }
  | { name: 'done'; result: Record<string, unknown> };

function initialStep(shift: ShiftView): Step {
  const cl = shift.closing;
  if (shift.status !== 'closing' || !cl) return { name: 'start' };
  const f = cl.final_count;
  if (f) {
    if (f.needs_reason)
      return { name: 'explain', counted: f.counted_minor, overShort: f.over_short_minor ?? 0 };
    // a takeover's counter gets no difference (it is the previous cashier's); none is shown then
    return {
      name: 'finish',
      counted: f.counted_minor,
      overShort: f.over_short_minor ?? 0,
      withinTolerance: f.within_tolerance ?? true,
      reason: null,
    };
  }
  return { name: 'count', final: shift.next_count_final, again: false };
}

/** Where a count flow is: start (and payments still pending), the count, then the finish (an
 * explanation when one is needed, what is kept, the close itself). */
const FLOW_STEPS = ['Start', 'Count', 'Finish'] as const;
function stepIndex(step: Step['name']): number {
  if (step === 'start' || step === 'pending') return 0;
  return step === 'count' ? 1 : 2;
}
function FlowSteps({ at }: { at: number }) {
  return (
    <ol className="shift-steps" aria-label="Steps">
      {FLOW_STEPS.map((label, i) => (
        <li
          key={label}
          className={`shift-steps__item${i < at ? ' shift-steps__item--done' : ''}`}
          aria-current={i === at ? 'step' : undefined}
        >
          {label}
        </li>
      ))}
    </ol>
  );
}

function CountFlow({
  mode,
  shift,
  state,
  currency,
  timeZone,
  onApproval,
  onExit,
}: {
  mode: FlowMode;
  shift: ShiftView;
  state: ShiftStateView;
  currency: string;
  timeZone: string;
  onApproval: (r: ApprovalRequest) => void;
  onExit: () => void;
}) {
  const send = useShiftCommand();
  const { toast } = useToast();
  const online = useOnline();
  const money = (minor: number) => formatMinor(minor, currency);
  const [step, setStep] = useState<Step>(() => initialStep(shift));
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [pendingReason, setPendingReason] = useState('');
  const [keep, setKeep] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const cashier = shift.cashier.name;
  const me = state.employee_id;

  const begin = async (why?: string, grant?: Approval) => {
    setBusy(true);
    setError(null);
    let r: Outcome;
    if (mode === 'takeover') {
      r = await send(`${shift.id}:start`, 'shift.takeover_start', {
        shift_id: shift.id,
        ...(grant ?? {}),
      });
    } else {
      // payments taken on this tablet reach the server before the drawer is counted
      await outbox.flush();
      if (outbox.sendingFor(me) > 0) {
        setBusy(false);
        setError(
          'Payments taken on this tablet are still being sent. Wait until it shows SYNCED, then start counting.',
        );
        return;
      }
      const held = heldOnThisTablet();
      r = await send(
        `${shift.id}:start`,
        mode === 'handover' ? 'shift.handover_start' : 'shift.close_start',
        {
          shift_id: shift.id,
          ...(why ? { pending_reason: why } : {}),
          ...(held.entries > 0 ? { parked_entries: held.entries, parked_minor: held.minor } : {}),
        },
      );
    }
    setBusy(false);
    if (r.ok) {
      setAmount('');
      setStep({
        name: 'count',
        // a takeover's recount rule carries over from this employee's earlier count on the shift
        final: mode === 'takeover' ? shift.next_count_final : r.result.next_count_final === true,
        again: false,
      });
      return;
    }
    const h = hintOf(r.error);
    if (h.requires === 'pending_reason' || h.requires === 'pending_wait') {
      const terminals = Array.isArray(h.terminals)
        ? (h.terminals as { name: string; pending: number }[])
        : [];
      setStep({
        name: 'pending',
        wait: h.requires === 'pending_wait',
        terminals,
        parked: Number(h.parked_entries ?? 0),
        message: r.error.message,
      });
      return;
    }
    if (
      mode === 'takeover' &&
      r.error.code === 'forbidden' &&
      h.requires === 'shift.takeover.approve'
    ) {
      onApproval(
        approvalRequest(
          'shift.takeover_start',
          { shift_id: shift.id },
          approvalCopy('shift.takeover.approve', r.error),
          (a) => begin(undefined, a),
        ),
      );
      return;
    }
    setError(r.error.message);
  };

  const submitCount = async (final: boolean) => {
    const minor = parseAmount(amount, currency);
    if (minor === null || minor < 0) {
      setError('Enter the cash counted, for example 2000 or 2000.50');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await send(`${shift.id}:count:${final ? 2 : 1}`, 'shift.count', {
      shift_id: shift.id,
      count_minor: minor,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    const c = r.result as unknown as CountResult;
    setAmount('');
    if (c.result === 'count_again') {
      setStep({ name: 'count', final: true, again: true });
      return;
    }
    if (c.needs_reason)
      setStep({ name: 'explain', counted: minor, overShort: c.over_short_minor ?? 0 });
    else
      setStep({
        name: 'finish',
        counted: minor,
        overShort: c.over_short_minor ?? 0,
        withinTolerance: c.within_tolerance ?? true,
        reason: null,
      });
  };

  const finish = async (
    s: Extract<Step, { name: 'finish' }>,
    keepMinor: number,
    grant?: Approval,
  ) => {
    setBusy(true);
    setError(null);
    let r: Outcome;
    let payload: CommandPayloads['shift.close_finish'] | null = null;
    if (mode === 'takeover') {
      r = await send(`${shift.id}:finish`, 'shift.takeover_finish', { shift_id: shift.id });
    } else {
      payload = {
        shift_id: shift.id,
        ...(mode === 'close' ? { retained_minor: keepMinor } : {}),
        ...(s.reason ? { variance_reason: s.reason } : {}),
      };
      r = await send(`${shift.id}:finish`, 'shift.close_finish', { ...payload, ...(grant ?? {}) });
    }
    setBusy(false);
    if (r.ok) {
      if (mode === 'takeover') {
        toast(`You now run this drawer. ${cashier}'s shift closed with your count.`, 'success');
        onExit();
        return;
      }
      setStep({ name: 'done', result: r.result });
      return;
    }
    const h = hintOf(r.error);
    if (payload && r.error.code === 'forbidden' && h.requires === 'cash.receive') {
      onApproval(
        approvalRequest('shift.close_finish', payload, approvalCopy('cash.receive', r.error), (a) =>
          finish(s, keepMinor, a),
        ),
      );
      return;
    }
    if (h.requires === 'variance_reason') {
      setStep({
        name: 'explain',
        counted: s.counted,
        overShort: Number(h.over_short_minor ?? s.overShort),
      });
      return;
    }
    setError(r.error.message);
  };

  const cancel = async () => {
    setBusy(true);
    const r = await send(`${shift.id}:cancel`, 'shift.close_cancel', { shift_id: shift.id });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    toast('Counting stopped; the drawer takes payments again');
    onExit();
  };

  // Before the final count anyone counting may stop; once it is in (its difference shown), only
  // the owner may cancel it, so nobody adjusts the drawer and counts again (server-enforced).
  const finalIn = step.name === 'explain' || step.name === 'finish';
  const mayStop = step.name === 'count' || (finalIn && !!state.permissions?.cover);
  const nextFinal = step.name === 'count' && (step.final || step.again);
  const cancelBlock = mayStop && (
    <div className="shift-flow__stop">
      {confirmCancel ? (
        <Notice
          role="alert"
          action={
            <span className="d-inline">
              <Button size="sm" variant="quiet" onClick={() => setConfirmCancel(false)}>
                Keep counting
              </Button>
              <Button
                size="sm"
                variant="danger"
                busy={busy}
                disabled={!online}
                onClick={() => void cancel()}
                data-testid="count-cancel-confirm"
              >
                Stop counting
              </Button>
            </span>
          }
        >
          {finalIn ? 'Cancel this close?' : 'Stop counting?'} The drawer takes payments again.
          {nextFinal ? ' Your next count on this shift will be final.' : ''}
        </Notice>
      ) : (
        <Button variant="quiet" onClick={() => setConfirmCancel(true)} data-testid="count-cancel">
          Stop counting
        </Button>
      )}
    </div>
  );
  const errorLine = error && (
    <div className="d-error" role="alert">
      {error}
    </div>
  );
  const steps = <FlowSteps at={stepIndex(step.name)} />;

  switch (step.name) {
    case 'start':
      return (
        <div className="d-stack" data-testid="close-start">
          {steps}
          <p className="d-muted">
            {mode === 'close' &&
              (shift.mine
                ? 'Count the drawer to close your shift. While you count, it takes no payments.'
                : `Count the drawer to close ${cashier}'s shift as the owner. While you count, it takes no payments.`)}
            {mode === 'handover' &&
              'Count the drawer now. Everything stays in it for the next cashier, who counts it again to open their shift.'}
            {mode === 'takeover' &&
              `Count the drawer. ${cashier}'s shift closes with your count and yours opens with the same cash.`}
          </p>
          {errorLine}
          <Button
            variant="primary"
            size="lg"
            block
            busy={busy}
            disabled={!online}
            onClick={() => void begin()}
            data-testid="close-begin"
          >
            Start counting
          </Button>
        </div>
      );
    case 'pending':
      return (
        <div className="d-stack" data-testid="close-pending">
          {steps}
          <Notice tone="warning">{step.message}.</Notice>
          {step.terminals.length > 0 && (
            <div className="d-kv">
              {step.terminals.map((t) => (
                <KvRow
                  key={t.name}
                  label={<span className="d-break">{t.name}</span>}
                  value={`${t.pending} ${t.pending === 1 ? 'payment' : 'payments'}`}
                />
              ))}
            </div>
          )}
          {step.parked > 0 && (
            <p className="d-help">
              On this tablet: {step.parked} not recorded yet (see the notices above the POS).
            </p>
          )}
          {step.wait && !state.permissions?.cover ? (
            <>
              {errorLine}
              <Button
                variant="primary"
                size="lg"
                block
                busy={busy}
                disabled={!online}
                onClick={() => void begin()}
              >
                Try again
              </Button>
            </>
          ) : (
            <>
              <TextField
                label="Why count anyway"
                value={pendingReason}
                onChange={setPendingReason}
                help="Kept with the count for the owner's review."
                testId="pending-reason"
                multiline
              />
              {errorLine}
              <div className="d-sheet__actions">
                <Button variant="quiet" busy={busy} disabled={!online} onClick={() => void begin()}>
                  Try again
                </Button>
                <Button
                  variant="primary"
                  busy={busy}
                  disabled={!online || !pendingReason.trim()}
                  onClick={() => void begin(pendingReason.trim())}
                  data-testid="pending-go"
                >
                  Count anyway
                </Button>
              </div>
            </>
          )}
        </div>
      );
    case 'count':
      return (
        <div className="d-stack" data-testid="close-count">
          {steps}
          {step.again ? (
            <Notice tone="warning" role="alert" testId="count-again">
              Please count again carefully. This count will be final.
            </Notice>
          ) : (
            <p className="d-muted">
              Count all the cash in the drawer and enter the total.
              {step.final ? <span className="d-strong"> This count is final.</span> : ''}
            </p>
          )}
          <MoneyField
            label="Cash counted"
            value={amount}
            onChange={setAmount}
            testId="close-count-input"
            large
          />
          {errorLine}
          <Button
            variant="primary"
            size="lg"
            block
            busy={busy}
            disabled={!online}
            onClick={() => void submitCount(step.final || step.again)}
            data-testid="close-count-submit"
          >
            Submit count
          </Button>
          {cancelBlock}
        </div>
      );
    case 'explain':
      return (
        <div className="d-stack" data-testid="close-explain">
          {steps}
          <div className="d-stats">
            <Stat label="Counted" value={money(step.counted)} />
            <Stat
              label="Difference"
              value={describeOverShort(step.overShort, money)}
              tone="danger"
            />
          </div>
          <TextField
            label="What happened"
            value={reason}
            onChange={setReason}
            help="Required: the difference is more than the tolerance. The owner sees it."
            testId="variance-reason"
            multiline
            maxLength={300}
          />
          {errorLine}
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!reason.trim()}
            onClick={() =>
              setStep({
                name: 'finish',
                counted: step.counted,
                overShort: step.overShort,
                withinTolerance: false,
                reason: reason.trim(),
              })
            }
            data-testid="variance-next"
          >
            Next
          </Button>
          {cancelBlock}
        </div>
      );
    case 'finish': {
      const suggested = suggestedKeepMinor(
        step.counted,
        state.standard_float_minor ?? 0,
        !!state.safe_enabled,
      );
      const keepMinor = keep === null ? suggested : parseAmount(keep, currency);
      const invalidKeep = keepMinor === null || keepMinor < 0 || keepMinor > step.counted;
      const out = invalidKeep ? 0 : removedMinor(step.counted, keepMinor);
      return (
        <div className="d-stack" data-testid="close-finish">
          {steps}
          <div className="d-stats">
            <Stat label="Counted" value={money(step.counted)} testId="close-counted" />
            {mode !== 'takeover' && (
              <Stat
                label="Difference"
                value={describeOverShort(step.overShort, money)}
                tone={step.withinTolerance ? overShortTone(step.overShort, Infinity) : 'danger'}
                testId="close-over-short"
              />
            )}
          </div>
          {mode === 'close' && (
            <>
              <MoneyField
                label="Keep in the drawer for the next shift"
                value={keep ?? minorToInput(suggested, fractionDigitsFor(currency))}
                onChange={setKeep}
                testId="close-keep"
              />
              {invalidKeep && (
                <div className="d-error" role="alert">
                  Keep between {money(0)} and {money(step.counted)}.
                </div>
              )}
              {out > 0 && (
                <Notice tone="info" testId="close-removed">
                  <span className="d-mono">{money(out)}</span> leaves the drawer{' '}
                  {state.safe_enabled
                    ? 'into a numbered safe envelope.'
                    : state.permissions?.receive
                      ? 'into your hands.'
                      : 'into the owner’s hands: the owner confirms the amount with their PIN.'}
                </Notice>
              )}
            </>
          )}
          {mode === 'handover' && (
            <p className="d-help">Everything stays in the drawer for the next cashier.</p>
          )}
          {mode === 'takeover' && (
            <p className="d-help">
              All of it stays in the drawer; any difference stays on {cashier}&apos;s shift.
            </p>
          )}
          {errorLine}
          <Button
            variant="primary"
            size="lg"
            block
            busy={busy}
            disabled={!online || (mode === 'close' && invalidKeep)}
            onClick={() => void finish(step, keepMinor ?? 0)}
            data-testid="close-finish-submit"
          >
            {mode === 'takeover'
              ? 'Start my shift'
              : mode === 'handover'
                ? 'Hand over'
                : 'Close shift'}
          </Button>
          {cancelBlock}
        </div>
      );
    }
    case 'done': {
      const r = step.result;
      const removed = Number(r.removed_minor ?? 0);
      return (
        <div className="d-stack" data-testid="close-done">
          <Notice tone="success" role="status">
            {mode === 'handover'
              ? 'Handed over. The next cashier signs in and counts the drawer to open their shift.'
              : 'Shift closed.'}
            {removed > 0 && r.removed_to === 'safe'
              ? ` Put ${money(removed)} in safe envelope #${String(r.envelope_no)}.`
              : ''}
            {removed > 0 && r.removed_to === 'owner'
              ? ` ${money(removed)} handed to the owner.`
              : ''}
          </Notice>
          {/* read back through shift_summary: the close result carries no summary */}
          <SummaryLoader shiftId={String(r.shift_id)} currency={currency} timeZone={timeZone} />
          <Button variant="primary" size="lg" block onClick={onExit} data-testid="close-done-ok">
            Done
          </Button>
        </div>
      );
    }
  }
}

/* ── cash entries: pay in, pay out, no sale, safe drop ────────────────────────────────── */

const MOVEMENT_TITLE: Record<'pay_in' | 'pay_out' | 'no_sale' | 'safe_drop', string> = {
  pay_in: 'Pay in',
  pay_out: 'Pay out',
  no_sale: 'No sale',
  safe_drop: 'Safe drop',
};

function MovementForm({
  kind,
  shift,
  state,
  currency,
  time,
  onDone,
}: {
  kind: 'pay_in' | 'pay_out' | 'no_sale' | 'safe_drop';
  shift: ShiftView;
  state: ShiftStateView;
  currency: string;
  time: (iso: string) => string;
  onDone: () => void;
}) {
  const send = useShiftCommand();
  const { toast } = useToast();
  const online = useOnline();
  const money = (minor: number) => formatMinor(minor, currency);
  const [amount, setAmount] = useState('');
  const [inCat, setInCat] = useState<PayInCategory | null>(null);
  const [outCat, setOutCat] = useState<PayOutCategory | null>(null);
  const [payee, setPayee] = useState('');
  const [reason, setReason] = useState('');
  const [receipt, setReceipt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const outCategories = PAY_OUT_CATEGORIES.filter(
    (c) => c !== 'owner_draw' || state.permissions?.receive,
  );
  // the amount as typed, said on the button that records it
  const typed = kind === 'no_sale' ? null : parseAmount(amount, currency);

  const submit = async () => {
    const minor = kind === 'no_sale' ? 0 : parseAmount(amount, currency);
    if (kind !== 'no_sale' && (minor === null || minor <= 0)) {
      setError('Enter the amount');
      return;
    }
    const why = reason.trim();
    let r: Outcome;
    setBusy(true);
    setError(null);
    if (kind === 'pay_in') {
      if (!inCat || !why) {
        setBusy(false);
        setError('Choose what it is and say why');
        return;
      }
      const payload = { amount_minor: minor!, category: inCat, reason: why };
      r = await send(`pay_in:${shift.id}:${JSON.stringify(payload)}`, 'cash.pay_in', payload);
    } else if (kind === 'pay_out') {
      if (!outCat || !why || !payee.trim()) {
        setBusy(false);
        setError('Choose what it is for, who was paid and why');
        return;
      }
      const payload = {
        amount_minor: minor!,
        category: outCat,
        payee: payee.trim(),
        reason: why,
        ...(receipt.trim() ? { receipt_ref: receipt.trim() } : {}),
      };
      r = await send(`pay_out:${shift.id}:${JSON.stringify(payload)}`, 'cash.pay_out', payload);
    } else if (kind === 'no_sale') {
      if (!why) {
        setBusy(false);
        setError('Say why the drawer is opened');
        return;
      }
      r = await send(`no_sale:${shift.id}:${why}`, 'cash.no_sale', { reason: why });
    } else {
      r = await send(`safe_drop:${shift.id}:${minor!}`, 'cash.safe_drop', {
        amount_minor: minor!,
      });
    }
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    if (kind === 'pay_out' && r.result.flagged)
      toast(`Pay-out of ${money(minor!)} recorded. The owner reviews it.`, 'success');
    else if (kind === 'safe_drop')
      toast(`Put ${money(minor!)} in safe envelope #${String(r.result.envelope_no)}`, 'success');
    else if (kind === 'no_sale') toast('No sale recorded: open the drawer', 'success');
    else toast(`${MOVEMENT_TITLE[kind]} of ${money(minor!)} recorded`, 'success');
    onDone();
  };

  const entries = (shift.movements ?? []).filter((m) =>
    kind === 'pay_in' || kind === 'pay_out' ? m.kind === kind : false,
  );
  return (
    <div className="d-stack" data-testid={`movement-${kind}`}>
      {kind !== 'no_sale' && (
        <MoneyField label="Amount" value={amount} onChange={setAmount} testId="movement-amount" />
      )}
      {kind === 'pay_in' && (
        <Choice
          label="What it is"
          value={inCat}
          options={PAY_IN_CATEGORIES}
          labels={PAY_IN_LABELS}
          onChange={setInCat}
          testId="movement-category"
        />
      )}
      {kind === 'pay_out' && (
        <>
          <Choice
            label="What it is for"
            value={outCat}
            options={outCategories}
            labels={PAY_OUT_LABELS}
            onChange={setOutCat}
            testId="movement-category"
          />
          <TextField
            label="Paid to"
            value={payee}
            onChange={setPayee}
            testId="movement-payee"
            maxLength={80}
          />
        </>
      )}
      {kind !== 'safe_drop' && (
        <TextField
          label={kind === 'no_sale' ? 'Why the drawer is opened' : 'Reason'}
          value={reason}
          onChange={setReason}
          testId="movement-reason"
        />
      )}
      {kind === 'pay_out' && (
        <TextField
          label="Receipt or reference (optional)"
          value={receipt}
          onChange={setReceipt}
          testId="movement-receipt"
          maxLength={64}
        />
      )}
      {kind === 'pay_out' && (state.payout_review_minor ?? 0) > 0 && (
        <p className="d-help">
          Pay-outs above {money(state.payout_review_minor ?? 0)} are reviewed by the owner.
        </p>
      )}
      {kind === 'safe_drop' && (
        <p className="d-help">The envelope gets a number: write it on the envelope.</p>
      )}
      {error && (
        <div className="d-error" role="alert">
          {error}
        </div>
      )}
      <Button
        variant="primary"
        size="lg"
        block
        busy={busy}
        disabled={!online}
        onClick={() => void submit()}
        data-testid="movement-submit"
      >
        {kind === 'no_sale'
          ? 'Record and open the drawer'
          : typed !== null && typed > 0
            ? `Record ${money(typed)}`
            : 'Record'}
      </Button>
      {entries.length > 0 && (
        <RecentEntries
          entries={entries}
          money={money}
          time={time}
          cover={!!state.permissions?.cover}
        />
      )}
    </div>
  );
}

function categoryLabel(kind: string, category: string): string {
  const labels: Record<string, string> = kind === 'pay_out' ? PAY_OUT_LABELS : PAY_IN_LABELS;
  return labels[category] ?? category;
}

/** This shift's latest pay-ins or pay-outs: a mistyped one is cancelled with a reason. */
function RecentEntries({
  entries,
  money,
  time,
  cover,
}: {
  entries: ShiftMovementView[];
  money: (minor: number) => string;
  time: (iso: string) => string;
  cover: boolean;
}) {
  const send = useShiftCommand();
  const { toast } = useToast();
  const online = useOnline();
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async (m: ShiftMovementView) => {
    setBusy(true);
    setError(null);
    const r = await send(`void:${m.id}`, 'cash.movement_void', {
      movement_id: m.id,
      reason: why.trim(),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    toast(`${ACTION_LABEL[m.kind] ?? m.kind} of ${money(m.amount_minor)} cancelled`);
    setCancelling(null);
    setWhy('');
  };

  return (
    <section
      className="d-stack d-stack--tight"
      aria-label="Recorded on this shift"
      data-testid="recent-entries"
    >
      <div className="d-eyebrow">Recorded on this shift</div>
      <div className="d-list">
        {entries.map((m) => (
          <div
            key={m.id}
            className={`d-row shift-entry${m.voided ? ' shift-entry--void' : ''}`}
            data-testid="recent-entry"
          >
            <div className="d-row__main">
              <div className="d-row__title">
                {m.category ? categoryLabel(m.kind, m.category) : (ACTION_LABEL[m.kind] ?? m.kind)}
                {m.payee ? ` · ${m.payee}` : ''}
              </div>
              <div className="d-row__meta">
                {time(m.recorded_at)} · {m.by}
                {m.reason ? ` · “${m.reason}”` : ''}
              </div>
              {cancelling === m.id && (
                <div className="d-stack d-stack--tight">
                  <TextField
                    label="Why cancel it"
                    value={why}
                    onChange={setWhy}
                    testId="entry-void-reason"
                  />
                  {error && (
                    <div className="d-error" role="alert">
                      {error}
                    </div>
                  )}
                  <span className="d-inline">
                    <Button size="sm" variant="quiet" onClick={() => setCancelling(null)}>
                      Back
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      busy={busy}
                      disabled={!online || !why.trim()}
                      onClick={() => void cancel(m)}
                      data-testid="entry-void-confirm"
                    >
                      Cancel entry of {money(m.amount_minor)}
                    </Button>
                  </span>
                </div>
              )}
            </div>
            <div className="shift-entry__side">
              <span className="d-mono shift-entry__amount">{money(m.amount_minor)}</span>
              {m.voided && <Badge tone={LEDGER_ROW_TONE.cancelled}>cancelled</Badge>}
              {!m.voided && (m.mine || cover) && cancelling !== m.id && (
                <Button
                  size="sm"
                  variant="quiet"
                  disabled={!online}
                  onClick={() => {
                    setCancelling(m.id);
                    setWhy('');
                    setError(null);
                  }}
                  data-testid="entry-void"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── notices ──────────────────────────────────────────────────────────────────────────── */

const ackKey = (shiftId: string) => `didto-os-shift-seen:${shiftId}`;
function readAcked(shiftId: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(ackKey(shiftId)) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as string[]) : [];
  } catch {
    return [];
  }
}

/** What the owner (or anyone else) did with the cashier's drawer money, until the cashier saw it. */
function OwnerActions({
  shift,
  money,
  time,
}: {
  shift: ShiftView;
  money: (minor: number) => string;
  time: (iso: string) => string;
}) {
  const [acked, setAcked] = useState<string[]>(() => readAcked(shift.id));
  const fresh = (shift.others ?? []).filter((x) => !acked.includes(x.id));
  if (fresh.length === 0) return null;
  const ack = () => {
    const next = [...acked, ...fresh.map((x) => x.id)];
    setAcked(next);
    try {
      localStorage.setItem(ackKey(shift.id), JSON.stringify(next));
    } catch {
      // storage unavailable: the notice comes back after a reload
    }
  };
  return (
    <Notice
      tone="info"
      role="status"
      testId="owner-actions"
      action={
        <Button size="sm" onClick={ack} data-testid="owner-actions-ok">
          Seen
        </Button>
      }
    >
      <div className="d-stack d-stack--tight">
        {fresh.map((x) => (
          <span key={x.id} className="d-break">
            {x.by ?? 'Someone'} recorded on your shift: {ACTION_LABEL[x.action] ?? x.action}{' '}
            <span className="d-mono">{money(x.amount_minor)}</span>
            {x.method
              ? ` (${x.method})`
              : x.category
                ? ` (${categoryLabel(x.action, x.category)})`
                : ''}{' '}
            · {time(x.at)}
            {x.cash ? '' : ' · no cash'}
          </span>
        ))}
      </div>
    </Notice>
  );
}

/**
 * Payments this tablet still holds: waiting for the cashier who took them to sign in again, or
 * refused by the server ("payment not recorded"): sent again, or discarded with a reason so the
 * owner sees it in the shift summary. Nothing is dropped silently.
 */
function QueueNotices({ money }: { money: (minor: number) => string }) {
  const box = useOutboxStatus();
  const failed = useFailedEntries();
  const send = useShiftCommand();
  const { toast } = useToast();
  const online = useOnline();
  const [discarding, setDiscarding] = useState<FailedEntry | null>(null);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retry = (f: FailedEntry) => {
    outbox.retry(f.entry.envelope.command_id).then(
      () => toast('Payment recorded', 'success'),
      (e) => toast(errorText(e), 'danger'),
    );
  };
  const discard = async () => {
    if (!discarding) return;
    const env = discarding.entry.envelope;
    const p = env.payload as { order_id?: string; shift_id?: string };
    setBusy(true);
    setError(null);
    const r = await send(`discard:${env.command_id}`, 'payment.discard_queued', {
      original_command_id: env.command_id,
      original_kind: env.kind,
      reason: why.trim(),
      ...(p.order_id ? { order_id: p.order_id } : {}),
      ...(p.shift_id ? { shift_id: p.shift_id } : {}),
      amount_minor: entryAmountMinor(discarding.entry),
      refusal: errorRecord(discarding.error).slice(0, 300),
      ...(discarding.entry.employeeId
        ? { queued_by_employee_id: discarding.entry.employeeId }
        : {}),
      payload: env.payload as unknown as Record<string, unknown>,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    outbox.drop(env.command_id);
    setDiscarding(null);
    setWhy('');
    toast('Discarded; the owner sees it in the shift summary');
  };

  return (
    <>
      {failed.map((f) => (
        <Notice
          key={f.entry.envelope.command_id}
          tone="danger"
          role="alert"
          testId="payment-not-recorded"
          action={
            <span className="d-inline">
              <Button size="sm" disabled={!online} onClick={() => retry(f)}>
                Send again
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={!online}
                onClick={() => {
                  setDiscarding(f);
                  setWhy('');
                  setError(null);
                }}
                data-testid="payment-discard"
              >
                Discard
              </Button>
            </span>
          }
        >
          Payment not recorded: <span className="d-mono">{money(entryAmountMinor(f.entry))}</span>
          {employeeNameOf(f.entry.employeeId)
            ? `, taken by ${employeeNameOf(f.entry.employeeId)}`
            : ''}
          . {errorText(f.error)}. If it cannot be recorded, discard it saying what happened.
        </Notice>
      ))}
      {box.parkedByEmployee.map((p) => (
        <Notice key={p.employeeId} tone="warning" role="status" testId="payments-parked">
          {p.count} {p.count === 1 ? 'payment waits' : 'payments wait'} for{' '}
          {employeeNameOf(p.employeeId) ?? 'the cashier who took them'} to sign in on this tablet.
        </Notice>
      ))}
      <Sheet open={!!discarding} onClose={() => setDiscarding(null)} title="Discard a payment">
        {discarding && (
          <div className="d-stack" data-testid="discard-sheet">
            <p className="d-muted">
              <strong className="d-mono">{money(entryAmountMinor(discarding.entry))}</strong> was
              not recorded. Discarding keeps a record of it with your reason; it does not record the
              payment.
            </p>
            <TextField
              label="What happened"
              value={why}
              onChange={setWhy}
              testId="discard-reason"
              multiline
            />
            {error && (
              <div className="d-error" role="alert">
                {error}
              </div>
            )}
            <Button
              variant="danger-solid"
              size="lg"
              block
              busy={busy}
              disabled={!online || !why.trim()}
              onClick={() => void discard()}
              data-testid="discard-confirm"
            >
              Discard {money(entryAmountMinor(discarding.entry))}
            </Button>
          </div>
        )}
      </Sheet>
    </>
  );
}

/* ── a closed shift's summary on the tablet ───────────────────────────────────────────── */

function SummaryLoader({
  shiftId,
  currency,
  timeZone,
}: {
  shiftId: string;
  currency: string;
  timeZone: string;
}) {
  const q = useQuery({
    queryKey: shiftKeys.summary(shiftId),
    queryFn: () => fetchShiftSummary(shiftId),
  });
  const v = q.data;
  // what was loaded stays on screen if a later read fails (the connection dropped, ADR-037)
  if (!v) {
    if (q.isError)
      return (
        <ErrorState
          title="Could not load the shift summary"
          message={errorText(q.error)}
          onRetry={() => void q.refetch()}
        />
      );
    return q.isPending ? <Loading rows={3} label="Loading the shift summary…" /> : null;
  }
  return (
    <ShiftSummaryView
      summary={v.frozen ?? v.live}
      live={v.live}
      currency={currency}
      timeZone={timeZone}
    />
  );
}
